import {
  LinkOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import { BriefDeliverableTaskUploadTypeEnum } from "@entities/brief/lib/constants";
import { useCreateDraftingComment } from "@entities/brief/lib/hooks/darfting/useCreateDraftingComment";
import { useDeleteDraftingComment } from "@entities/brief/lib/hooks/darfting/useDeleteDraftingComment";
import { useGetDraftById } from "@entities/brief/lib/hooks/darfting/useGetDraftById";
// import { useReplyToDraftingComment } from "@entities/brief/lib/hooks/darfting/useReplyToDraftingComment";
import useGetMediaAssets from "@entities/brief/lib/hooks/darfting/useGetMediaAssets";
import { useResolveDraftingComment } from "@entities/brief/lib/hooks/darfting/useResolveDraftingComment";
import UploadModal from "@entities/videoPlayerV2/ui/UploadModal";
import {
  IDraftingContents,
  IDraftingDrawings,
  IDraftingMediaAssets,
} from "@shared/lib/types/drafting";
import { Modal } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Col, Flex, Row, Space } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { DraftFeedback } from "./DraftFeedback/DraftFeedback";
import { DraftFooter } from "./DraftFooter";
import { DraftHeader } from "./DraftHeader/DraftHeader";
import { DraftMediaPreview } from "./DraftMediaPreview";
import { DraftPageSkeleton } from "./DraftPageSkeleton";

dayjs.extend(relativeTime);

const assetIcon = (type?: BriefDeliverableTaskUploadTypeEnum) => {
  switch (type) {
    case BriefDeliverableTaskUploadTypeEnum.Image:
      return <PictureOutlined />;
    case BriefDeliverableTaskUploadTypeEnum.YoutubeLink:
      return <YoutubeOutlined />;
    case BriefDeliverableTaskUploadTypeEnum.Video:
      return <VideoCameraOutlined />;
    default:
      return <LinkOutlined />;
  }
};

export function DraftPage({
  draftingId,
  onClose,
  refetchInfluencer,
}: {
  draftingId: string;
  onClose: () => void;
  refetchInfluencer: () => void;
}) {
  // const [searchParams, setSearchParams] = useSearchParams();

  // const draftingVersion = searchParams.get("draftingVersion") || undefined;

  // const mediaAssetIdParam = searchParams.get("mediaAsset") || undefined;

  const {
    drafting: draftingData,
    isFetching,
    isLoading,
    refetch,
  } = useGetDraftById(draftingId || "");

  const location = useLocation();

  const [selectedVersionId, setSelectedVersionId] = useState<string>();
  const [selectedAssetId, setSelectedAssetId] = useState<string>();
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);
  const [draftDrawings, setDraftDrawings] = useState<IDraftingDrawings[]>([]);

  const { createComment, isCreating, isCreated } = useCreateDraftingComment(
    draftingId || ""
  );
  // const {
  //   replyComment,
  //   isReplying,
  //   variables: replyVariables,
  //   isReplied,
  // } = useReplyToDraftingComment(draftingId || "");
  const { resolveComment, isResolving } = useResolveDraftingComment(
    draftingId || ""
  );
  const { deleteComment, isDeleting } = useDeleteDraftingComment(
    draftingId || ""
  );

  const { mediaAssetsInfo, isMediaAssetsFetching } = useGetMediaAssets({
    draftingId: draftingId || "",
    mediaAssetId: selectedAssetId || "",
    draftingVersionId: selectedVersionId || "",
  });

  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isGeneralMode, setIsGeneralMode] = useState(true);
  const [loadVersion, setLoadVersion] = useState<boolean>(false);

  const drafting = draftingData?.drafting;

  const firstRender = useRef<boolean>(true);

  const latestVersionId = drafting?.latestDraftingVersionId;
  const latestVersion = drafting?.draftingContents?.find(
    (item) => item._id === latestVersionId
  );
  const mediaAssetId = latestVersion?.mediaAssets?.[0]?._id;

  useEffect(() => {
    if (!latestVersion) {
      return;
    } else if (firstRender.current) {
      setSelectedVersionId(latestVersionId);
      setSelectedAssetId(mediaAssetId);
      firstRender.current = false;
      return;
    }
  }, [latestVersionId, mediaAssetId]);

  useEffect(() => {
    if (!isFetching && loadVersion) {
      setSelectedVersionId(latestVersionId);
      setSelectedAssetId(mediaAssetId);
      setLoadVersion(false);
    }
  }, [loadVersion, isFetching, latestVersionId, mediaAssetId]);

  useEffect(() => {
    return () => {
      firstRender.current = true;
    };
  }, []);

  const selectedVersion: IDraftingContents | undefined = useMemo(() => {
    return drafting?.draftingContents?.find(
      (item) => item._id === selectedVersionId
    );
  }, [drafting, selectedVersionId]);

  const selectedAsset: IDraftingMediaAssets | undefined = useMemo(() => {
    return selectedVersion?.mediaAssets?.find(
      (asset) => asset._id === selectedAssetId
    );
  }, [selectedVersion, selectedAssetId]);

  const assetComments = useMemo(() => {
    if (!selectedVersion) return [];
    return (selectedVersion.comments || []).filter(
      (c) => c.mediaAssetId === selectedAssetId || c.mediaAssetId === undefined
    );
  }, [selectedVersion, selectedAssetId]);

  const handleAddComment = (text: string, isGeneralMode: boolean) => {
    if (!selectedVersion || !drafting) return;
    if (!text.trim()) return;

    createComment({
      draftingId: drafting._id,
      draftingVersionId: selectedVersion._id,
      comments: {
        text: text.trim(),
        timestamp: isGeneralMode ? -1 : currentTime,
        drawings: draftDrawings.length > 0 ? [...draftDrawings] : undefined,
        isGeneral: isGeneralMode,
        mediaAssetId: selectedAssetId,
        clientCommentId: `client-${Date.now()}`,
      },
    });
    setIsDrawingEnabled(false);
    setDraftDrawings([]);
  };

  const handleResolve = (commentId: string, isResolved: boolean) => {
    if (!drafting || !selectedVersion) return;
    resolveComment({
      draftingId: drafting._id,
      draftingVersionId: selectedVersion._id,
      commentId,
      isResolved,
    });
  };

  const handleDelete = (commentId: string) => {
    if (!drafting || !selectedVersion) return;
    deleteComment({
      draftingId: drafting._id,
      draftingVersionId: selectedVersion._id,
      commentId,
    });
  };

  const handleJump = (time: number) => {
    setCurrentTime(time);
  };
  const handleTimeChange = (val: number) => {
    setCurrentTime(val);
    // Only clear if we are NOT in drawing mode (i.e. user scrubbed while drawing)
    // or if the user explicitly wants to clear.
    // Actually, simply scrubbing shouldn't necessarily clear draft drawings
    // if we are in the middle of creating a comment, but for now let's keep it simple:
    // If we move time, we might want to see drawings at that time (overlayDrawing handles this).
    // We shouldn't auto-clear draftDrawings if the user is just looking around,
    // unless they start drawing elsewhere.

    // For now, let's NOT clear automatically on time change to allow users
    // to pause, draw, scrub, draw more (maybe?).
    // But usually drawings are tied to a specific frame.
    // If the user scrubs, they are looking at a different frame.
    if (isDrawingEnabled && draftDrawings.length > 0) {
      setDraftDrawings([]);
      setIsDrawingEnabled(false);
    }
  };

  const isTimeNear = (ts: number | undefined, current: number) => {
    if (ts === undefined || ts < 0) return false;
    return Math.abs(ts - current) <= 0.6; // ~0.6s window
  };

  const overlayDrawing = useMemo(() => {
    const isImage =
      selectedAsset?.type === BriefDeliverableTaskUploadTypeEnum.Image;

    if (isImage) {
      const selected = assetComments.find(
        (c) => c._id === activeCommentId && c.drawings?.length
      );
      if (selected?.drawings?.length) return selected.drawings;

      // REMOVED: Auto-showing first drawing for images
      // const firstWithDrawing = assetComments.find((c) => c.drawings?.length);
      // return firstWithDrawing?.drawings || null;
      return null;
    }

    const selected = assetComments.find(
      (c) => c._id === activeCommentId && c.drawings?.length
    );

    // Only show drawing if explicitly selected via activeCommentId AND time matches
    if (selected && isTimeNear(selected.timestamp, currentTime)) {
      return selected.drawings || null;
    }

    // REMOVED: Auto-showing drawings based on time match
    // const timeMatched = assetComments.find(
    //   (c) => c.drawings?.length && isTimeNear(c.timestamp, currentTime)
    // );
    // return timeMatched?.drawings || null;

    return null;
  }, [
    activeCommentId,
    assetComments,
    currentTime,
    selectedAsset?.type,
    isTimeNear,
  ]);

  if (isLoading || !drafting) {
    return <DraftPageSkeleton />;
  }

  return (
    <>
      <Space
        direction="vertical"
        size={0}
        style={{ width: "100%", height: "100vh" }}
      >
        <DraftHeader
          refetch={() => {
            refetch();
            refetchInfluencer();
          }}
          handleUploadNewVersion={() => {
            setOpenModal(true);
          }}
          versions={drafting.draftingContents}
          selectedVersionId={selectedVersionId}
          status={draftingData?.campaignInfluencerTaskState?.status}
          onSelectVersion={(id) => {
            const version = drafting.draftingContents?.find(
              (v) => v._id === id
            );
            setSelectedVersionId(id);
            setSelectedAssetId(version?.mediaAssets?.[0]?._id || "");
            setCurrentTime(0);
          }}
          title={drafting.brief?.name || "Brief Name"}
          subtitle={drafting.brief?.brand?.name || "Brand Name"}
          draft={draftingData}
          selectedAssetId={selectedAssetId}
          onClose={onClose}
        />
        <Row
          wrap={false}
          style={{ alignItems: "stretch", height: "calc(100vh - 76px - 68px)" }}
        >
          <Col span={16} style={{ padding: "0" }}>
            <Flex vertical flex={1} style={{ width: "100%", height: "100%" }}>
              <div
                style={{
                  background: "#1a1f36",
                  padding: "12px 16px",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Space
                  size="small"
                  style={{ width: "100%", overflowX: "auto" }}
                >
                  {selectedVersion?.mediaAssets?.map((asset) => {
                    const isActive = asset._id === selectedAssetId;
                    return (
                      <Button
                        key={asset._id}
                        type={isActive ? "primary" : "text"}
                        icon={assetIcon(asset.type)}
                        onClick={() => {
                          setSelectedAssetId(asset._id);
                          // queryClient.resetQueries({
                          //   queryKey: generateMediaAssetsKey({
                          //     draftingId: draftingId || "",
                          //     draftingVersionId: selectedVersionId || "",
                          //     mediaAssetId: asset._id,
                          //   }),
                          // });
                        }}
                        style={{
                          background: isActive
                            ? "#4f46e5"
                            : "rgba(255,255,255,0.05)",
                          border: isActive
                            ? "1px solid #4f46e5"
                            : "1px solid rgba(255,255,255,0.08)",
                          color: isActive ? "#fff" : "#9ca3af",
                          height: 30,
                          padding: "0 16px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <TextWithTooltip text={asset.name} limit={10} />
                      </Button>
                    );
                  })}
                </Space>
              </div>
              <DraftMediaPreview
                draftingId={drafting?._id}
                draftingVersionId={selectedVersionId || ""}
                mediaAssetId={selectedAssetId || ""}
                asset={selectedAsset}
                currentTime={currentTime}
                onTimeChange={handleTimeChange}
                onAddCommentAt={(time) => {
                  // message.info(`Timestamp locked at ${formatTime(time)}`);
                  setIsGeneralMode(false);
                }}
                isDrawingEnabled={isDrawingEnabled}
                draftDrawings={draftDrawings}
                activeDrawing={overlayDrawing}
                comments={assetComments}
                onUpdateDraftDrawings={setDraftDrawings}
              />
            </Flex>
          </Col>
          <Col
            span={8}
            style={{ height: "100%", display: "flex", padding: "0 " }}
          >
            <DraftFeedback
              key={location.pathname}
              comments={assetComments}
              currentTime={currentTime}
              onPost={handleAddComment}
              onJump={handleJump}
              isDrawingEnabled={isDrawingEnabled}
              onDrawingEnabledChange={setIsDrawingEnabled}
              onClearDraftDrawings={() => setDraftDrawings([])}
              onSelectComment={(commentId, time) => {
                setActiveCommentId(commentId);
                setCurrentTime(time);
                setIsDrawingEnabled(false);
                setDraftDrawings([]);
              }}
              isCreating={isCreating}
              isOtherAsset={
                selectedAsset?.type ===
                BriefDeliverableTaskUploadTypeEnum.MediaLink
              }
              isTimeFrameAsset={
                selectedAsset?.type
                  ? [
                      BriefDeliverableTaskUploadTypeEnum.Video,
                      BriefDeliverableTaskUploadTypeEnum.YoutubeLink,
                    ].includes(selectedAsset?.type)
                  : false
              }
              draftingId={drafting?._id}
              draftingVersionId={selectedVersionId || ""}
              mediaAssetId={selectedAssetId || ""}
              isGeneralMode={isGeneralMode}
              setIsGeneralMode={setIsGeneralMode}
              onResolve={handleResolve}
              isResolving={isResolving}
              onDelete={handleDelete}
              isDeleting={isDeleting}
              transcription={mediaAssetsInfo?.data?.transcription}
              transcriptionStatus={mediaAssetsInfo?.data?.transcriptionStatus}
            />
          </Col>
        </Row>
        <DraftFooter
          draft={draftingData}
          isLatestVersion={selectedVersionId === latestVersionId}
          taskStatus={draftingData?.campaignInfluencerTaskState?.status}
          campaignInfluencer={draftingData?.campaignInfluencer}
          onSwitchToLatest={() => {
            if (latestVersionId) {
              setSelectedVersionId(latestVersionId);

              setTimeout(() => {
                const latest = drafting?.draftingContents?.find(
                  (v) => v._id === latestVersionId
                );
                setSelectedAssetId(latest?.mediaAssets?.[0]?._id || "");
                setCurrentTime(0);
              }, 3000);
            }
          }}
          onUploadNewVersion={() => setOpenModal(true)}
          unresolvedCommentCount={
            selectedVersion
              ? (selectedVersion.comments || []).filter((c) => !c.isResolved)
                  .length
              : 0
          }
          briefId={drafting?.brief?._id || ""}
          campaignInfluencerId={draftingData?.campaignInfluencer?._id || ""}
          deliverableConfigRefId={
            draftingData?.campaignInfluencerDeliverableState?._id || ""
          }
          taskId={draftingData?.campaignInfluencerTaskState?._id || ""}
          onRefetch={() => {
            refetch();
            refetchInfluencer();
          }}
        />
      </Space>
      <Modal
        open={openModal}
        footer={null}
        onCancel={() => setOpenModal(false)}
      >
        <UploadModal
          refetch={() => {
            refetch();
            refetchInfluencer();
            setLoadVersion(true);
          }}
          draftingId={draftingId}
          taskId={drafting?.campaignTaskId || ""}
          campaignInfluencerId={draftingData?.campaignInfluencer?._id}
          deliverableConfigRefId={
            draftingData?.campaignInfluencerDeliverableState?._id
          }
          onClose={() => setOpenModal(false)}
          // setLatestVersionId={setSelectedVersionId}
        />
      </Modal>
    </>
  );
}
